import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import paymentSessionService, {
  SESSION_ERROR,
  SESSION_STATUS,
} from '../services/paymentSessionService.js';
import OnrampPayTab from '../component/OnrampPayTab.js';
import CryptoPayTab from '../component/CryptoPayTab.js';
import PaymentMethodChooser from '../component/PaymentMethodChooser.js';
import cryptoPaymentService from '../services/cryptoPaymentService.js';
import './PaymentPage.css';

const CURRENCY_SYMBOLS = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  AUD: 'A$',
  CAD: 'C$',
  JPY: '¥',
  CHF: 'CHF',
  SGD: 'S$',
  HKD: 'HK$',
  AED: 'د.إ',
  THB: '฿',
  PHP: '₱',
  MYR: 'RM',
  KRW: '₩',
  BRL: 'R$',
  MXN: 'MX$',
  IDR: 'Rp',
  VND: '₫',
  NZD: 'NZ$',
};

const PAYMENT_MODE = {
  FIAT: 'fiat',
  CRYPTO: 'crypto',
};

// Status values that indicate the customer has committed to a rail.
// Once committed, the "Change method" link is hidden so they can't
// accidentally try to pay twice.
const COMMITTED_CARD_STATUSES = new Set([
  'AWAITING_PAYMENT',
  'PAYMENT_VERIFYING',
  'PROCESSING',
]);

const TERMINAL_SESSION_STATUSES = new Set([
  SESSION_STATUS.COMPLETED,
  SESSION_STATUS.FAILED,
  SESSION_STATUS.EXPIRED,
  SESSION_STATUS.CANCELLED,
]);

export default function PaymentPage() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  // chosenMethod controls what's shown after the chooser:
  //   null     → render PaymentMethodChooser (only when both rails enabled)
  //   'card'   → render OnrampPayTab
  //   'crypto' → render CryptoPayTab
  // For single-rail links, an effect sets this immediately on session load
  // so the chooser is skipped.
  const [chosenMethod, setChosenMethod] = useState(null);
  // Filled in by PaymentMethodChooser (or auto-commit for crypto-only) so
  // CryptoPayTab can render the QR. May arrive on session.cryptoDefault for
  // crypto-only links where the backend pre-generates at order create.
  const [cryptoDefault, setCryptoDefault] = useState(null);
  // Spinner shown while we auto-commit on a crypto-only link with no
  // pre-generated cryptoDefault (rare — server pre-gen failed).
  const [autoCommitting, setAutoCommitting] = useState(false);
  const [autoCommitError, setAutoCommitError] = useState(null);
  // Crypto deposit detected? Latched true once filledAmount > 0 so the
  // "Change method" link is hidden the moment funds arrive.
  const [cryptoCommitted, setCryptoCommitted] = useState(false);

  const validateSession = useCallback(async () => {
    if (!token) {
      setError({ code: 'INVALID_TOKEN', message: 'Invalid payment link' });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await paymentSessionService.validateSession(token);

      if (!result?.valid) {
        setError({ code: result?.errorCode, message: result?.error });
        setSession(null);
        return;
      }

      setSession(result.session);
      setTimeRemaining(result.session.expiresInSeconds);
      // Pre-populated by backend for crypto-only links (orders.service
      // pre-generates the address at order create). Mixed-rail links
      // start with cryptoDefault unset; PaymentMethodChooser populates it
      // via the /crypto/commit call.
      if (result.session?.cryptoDefault) {
        setCryptoDefault(result.session.cryptoDefault);
      }
      setError(null);
    } catch (err) {
      setError({
        code: 'VALIDATION_ERROR',
        message: err.message || 'Failed to validate payment session',
      });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    validateSession();
  }, [validateSession]);

  const visibleModes = useMemo(() => {
    const cardEnabled = session?.cardEnabled !== false;
    const cryptoEnabled = session?.cryptoEnabled !== false;
    return {
      cardEnabled,
      cryptoEnabled,
      bothEnabled: cardEnabled && cryptoEnabled,
    };
  }, [session?.cardEnabled, session?.cryptoEnabled]);

  // Drive chosenMethod from session + visibleModes:
  //   - both rails    → null (chooser screen takes over)
  //   - card-only     → 'card'  (skip chooser)
  //   - crypto-only   → 'crypto' (skip chooser)
  // Once the customer makes a choice, we don't reset it here.
  useEffect(() => {
    if (!session) return;
    if (chosenMethod) return;
    if (visibleModes.cardEnabled && !visibleModes.cryptoEnabled) {
      setChosenMethod('card');
    } else if (visibleModes.cryptoEnabled && !visibleModes.cardEnabled) {
      setChosenMethod('crypto');
    }
  }, [session?.orderId, visibleModes, chosenMethod]);

  // For crypto-only links: if backend pre-gen failed at order creation
  // (cryptoDefault not on session), auto-call /crypto/commit so the
  // customer doesn't see a stuck loading state.
  useEffect(() => {
    if (!session) return;
    if (chosenMethod !== 'crypto') return;
    if (cryptoDefault) return;
    if (autoCommitting) return;
    let cancelled = false;
    setAutoCommitting(true);
    cryptoPaymentService
      .commit(token)
      .then((result) => {
        if (cancelled) return;
        setCryptoDefault({
          tokenId: result.tokenId,
          tokenSymbol: result.tokenSymbol,
          network: result.network,
          networkDisplayName: result.networkDisplayName,
          depositAddress: result.depositAddress,
          expectedAmount: result.expectedAmount,
          decimals: result.decimals,
          qrPayload: result.qrPayload,
          minConfirmations: result.minConfirmations,
        });
        setAutoCommitError(null);
      })
      .catch((err) => {
        if (!cancelled) {
          setAutoCommitError(
            err?.message || 'Failed to prepare crypto payment.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setAutoCommitting(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, session?.orderId, chosenMethod, cryptoDefault, autoCommitting]);

  useEffect(() => {
    if (!session || timeRemaining <= 0) return;

    const pauseStatuses = [
      SESSION_STATUS.AWAITING_PAYMENT,
      SESSION_STATUS.PAYMENT_VERIFYING,
      SESSION_STATUS.PROCESSING,
      SESSION_STATUS.COMPLETED,
    ];
    if (pauseStatuses.includes(session.status)) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          setError({
            code: SESSION_ERROR.EXPIRED,
            message: 'Payment session has expired',
          });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [session, timeRemaining]);

  useEffect(() => {
    if (!token || !session) return;

    // Only poll session status while the user is on the card flow AND payment is mid-flight.
    // CryptoPayTab has its own /crypto/status poll — polling both endpoints
    // here doubles request volume and trips the per-IP rate limit.
    const shouldPollFiatRecovery =
      chosenMethod !== 'crypto' &&
      (session.status === SESSION_STATUS.AWAITING_PAYMENT ||
        session.status === SESSION_STATUS.PROCESSING ||
        session.status === SESSION_STATUS.PAYMENT_VERIFYING);

    if (!shouldPollFiatRecovery) return;

    let cancelled = false;
    const syncStatus = async () => {
      try {
        const result = await paymentSessionService.getSessionStatus(token);
        if (cancelled) return;
        setSession((prev) => (prev ? { ...prev, status: result.status } : prev));

        if (
          result.status === SESSION_STATUS.FAILED ||
          result.status === SESSION_STATUS.CANCELLED
        ) {
          setError({
            code: 'PAYMENT_FAILED',
            message: 'Payment was not completed. Please try again.',
          });
        }
      } catch (statusError) {
        console.warn('Status check failed:', statusError);
      }
    };

    syncStatus();
    const interval = setInterval(syncStatus, 15000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [token, session?.status, chosenMethod]);

  const handleEmbeddedStatusChange = useCallback((nextStatusOrSession) => {
    if (!nextStatusOrSession) return;

    // String status (legacy callers) — just update session.status.
    if (typeof nextStatusOrSession === 'string') {
      setSession((prev) =>
        prev ? { ...prev, status: nextStatusOrSession } : prev,
      );
      return;
    }

    // Crypto status object from CryptoPayTab — detect commitment via filledAmount.
    // Latch cryptoCommitted = true so the "Change method" link disappears
    // even if a later poll temporarily returns 0.
    const filledAmount = Number(nextStatusOrSession?.filledAmount) || 0;
    if (filledAmount > 0) {
      setCryptoCommitted(true);
    }

    // Status field on the object — apply to session only if it actually changed.
    // Without the equality guard, every poll creates a new session object reference,
    // which cascades into a re-render loop in child tabs that mirror the prop into state.
    if (nextStatusOrSession?.status) {
      setSession((prev) => {
        if (!prev) return prev;
        if (prev.status === nextStatusOrSession.status) return prev;
        return { ...prev, status: nextStatusOrSession.status };
      });
      return;
    }

    // Full session object replacement (unusual path).
    setSession(nextStatusOrSession);
  }, []);

  // "Change method" link: shown only when both rails are enabled AND the
  // customer hasn't committed yet. Card commitment = AWAITING_PAYMENT (etc.);
  // crypto commitment = first deposit detected via status poll.
  const canChangeMethod =
    visibleModes.bothEnabled &&
    chosenMethod !== null &&
    !COMMITTED_CARD_STATUSES.has(session?.status) &&
    !cryptoCommitted;

  const handleChangeMethod = useCallback(() => {
    setChosenMethod(null);
  }, []);

  const formatTime = (seconds) => {
    if (seconds <= 0) return '0:00';
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m ${secs.toString().padStart(2, '0')}s`;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatAmount = (amount, currency) => {
    const symbol = CURRENCY_SYMBOLS[currency] || '';
    return `${symbol}${parseFloat(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  if (loading) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-loading">
            <div className="loading-spinner"></div>
            <p className="loading-text">Loading payment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (
    session?.status === SESSION_STATUS.COMPLETED ||
    error?.code === SESSION_ERROR.COMPLETED
  ) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-header">
            <div className="payment-logo">KuberCrypto</div>
          </div>
          <div className="payment-success">
            <svg className="success-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="9,12 11,14 15,10" />
            </svg>
            <h2 className="success-title">Payment Successful!</h2>
            <p className="success-message">
              {session
                ? `Your payment of ${formatAmount(session.amount, session.currency)} has been processed successfully.`
                : 'Your payment has been processed successfully.'}
            </p>
            {session?.externalOrderId && (
              <p className="success-order-id">Order: {session.externalOrderId}</p>
            )}
            {session?.redirectUrl ? (
              <p className="success-redirect">Redirecting you back to the merchant...</p>
            ) : (
              <p className="success-hint">You can safely close this page.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    const isExpired = error.code === SESSION_ERROR.EXPIRED;

    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-header">
            <div className="payment-logo">KuberCrypto</div>
          </div>
          <div className="payment-error">
            <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              {isExpired ? (
                <><circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/></>
              ) : (
                <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>
              )}
            </svg>
            <h2 className="error-title">
              {isExpired ? 'Session Expired' : 'Invalid Session'}
            </h2>
            <p className="error-message">{error.message}</p>
            {isExpired && (
              <p className="error-hint">Please contact the merchant for a new payment link.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (
    session?.status === SESSION_STATUS.FAILED ||
    session?.status === SESSION_STATUS.CANCELLED
  ) {
    return (
      <div className="payment-page">
        <div className="payment-container">
          <div className="payment-header">
            <div className="payment-logo">KuberCrypto</div>
          </div>
          <div className="payment-error">
            <svg className="error-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="15" y1="9" x2="9" y2="15"/>
              <line x1="9" y1="9" x2="15" y2="15"/>
            </svg>
            <h2 className="error-title">Payment Not Completed</h2>
            <p className="error-message">
              {session.status === SESSION_STATUS.CANCELLED
                ? 'This payment was cancelled.'
                : 'The payment could not be completed. Please try again or contact the merchant.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  const handleChooserPick = (result) => {
    setChosenMethod(result.method);
    if (result.cryptoDefault) {
      setCryptoDefault(result.cryptoDefault);
    }
  };

  return (
    <div className="payment-page">
      <div className="payment-page-container">
        <div
          className={`payment-timer ${timeRemaining <= 300 && timeRemaining > 60 ? 'warning' : ''} ${timeRemaining <= 60 ? 'critical' : ''}`}
        >
          <svg className="timer-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12,6 12,12 16,14"/>
          </svg>
          <span className="timer-text">
            Session expires in: <strong>{formatTime(timeRemaining)}</strong>
          </span>
        </div>

        {session?.externalOrderId && (
          <div className="order-info-banner">
            Order: <span className="order-id">{session.externalOrderId}</span>
          </div>
        )}

        {chosenMethod === null ? (
          <PaymentMethodChooser
            token={token}
            session={session}
            onPick={handleChooserPick}
          />
        ) : (
          <div className="payment-mode-panel">
            {canChangeMethod && (
              <button
                type="button"
                className="payment-change-method"
                onClick={handleChangeMethod}
              >
                ← Change payment method
              </button>
            )}
            {chosenMethod === 'card' ? (
              <OnrampPayTab
                token={token}
                session={session}
                onSessionChange={handleEmbeddedStatusChange}
              />
            ) : autoCommitting ? (
              <div className="payment-loading">
                <div className="loading-spinner" />
                <p className="loading-text">Preparing crypto payment…</p>
              </div>
            ) : autoCommitError && !cryptoDefault ? (
              <div className="payment-error">
                <p className="error-message">{autoCommitError}</p>
              </div>
            ) : (
              <CryptoPayTab
                token={token}
                session={session}
                defaultAddress={cryptoDefault || session?.cryptoDefault || null}
                onStatusChange={handleEmbeddedStatusChange}
              />
            )}
          </div>
        )}

        <div className="payment-page-footer">
          Secured by{' '}
          <a href="https://kubercrypto.io" target="_blank" rel="noopener noreferrer">
            KuberCrypto
          </a>
        </div>
      </div>
    </div>
  );
}
